import { useEffect, useState } from 'react'
import { ArrowRight, BookOpen, Layers, Sparkles, X } from 'lucide-react'
import type { TenantInstanceClaude, SurfaceId } from '@omni/instance'
import { activeInstance } from '../instances'
import { productName } from '../lib/config'
import { shellNavigate } from '../lib/navigation'
import { useSurfaceVisibility } from '../surfaces'
import { AskCard, CLAUDE_URL } from './ask'
import { MissionControl } from './MissionControl'
import { HowItsBuiltHost, openHowItsBuilt } from './HowItsBuilt'
import { WelcomeHost, openWelcome } from './Welcome'
import './claude.css'

// The Claude button on every page opens this (Sam, 10 Oct 2026): the
// picture of the screens and the chat, what to ask about THIS page, and how to
// start a chat in the right folder. One panel for every Command Centre; the
// words come from the instance's `claude` settings.

export const instanceClaude: TenantInstanceClaude | undefined = activeInstance.claude

// Every Command Centre's Ask Claude button carries our mark (Sam, 18 Oct
// 2026: "customers too"), the one place a customer build shows it. Served as
// /ask-mark.png so the customer deploys' own-branding purge keeps it.
const DEFAULT_MARK = '/ask-mark.png'

function RailMark({ size }: { size: number }) {
  const mark = instanceClaude?.mark ?? DEFAULT_MARK
  if (mark) return <img src={mark} alt="" className="oc-rail-mark" style={{ width: size, height: size }} />
  return <Sparkles style={{ width: size, height: size }} />
}

/** The rail button. Expanded rail: a labelled card; collapsed: an icon. */
export function ClaudeRailButton({ collapsed, onOpen }: { collapsed: boolean; onOpen: () => void }) {
  // Someone who may not open Using Claude (e.g. an outside partner) has no
  // Claude to ask, so they get no button either.
  const canSee = useSurfaceVisibility()
  if (!instanceClaude || canSee('learn') !== true) return null
  if (!collapsed && instanceClaude.rail === 'plain') {
    return (
      <button type="button" className="oc-rail-plain" onClick={onOpen}>
        <RailMark size={16} />
        <span>Ask Claude</span>
      </button>
    )
  }
  if (collapsed) {
    return (
      <button type="button" className="oc-rail-claude oc-rail-claude-icon" onClick={onOpen} title="Ask Claude" aria-label="Ask Claude">
        <RailMark size={18} />
      </button>
    )
  }
  return (
    <>
    <button type="button" className="oc-rail-claude" onClick={onOpen}>
      <RailMark size={22} />
      <span>
        <b>Ask Claude</b>
        <small>What to ask about this page</small>
      </span>
    </button>
    {instanceClaude.welcome ? (
      <button type="button" className="oc-rail-built oc-rail-how" onClick={openWelcome}>
        <Layers className="h-3.5 w-3.5" /> How it works
      </button>
    ) : (
      <button type="button" className="oc-rail-built" onClick={openHowItsBuilt}>
        <Layers className="h-3.5 w-3.5" /> How it's built
      </button>
    )}
    </>
  )
}

/** The small corner icon, for instances with `cornerIcons` (MBA first, 30 Oct 2026). */
export function ClaudeCornerButton({ onOpen }: { onOpen: () => void }) {
  const canSee = useSurfaceVisibility()
  if (!instanceClaude || canSee('learn') !== true) return null
  return (
    <button
      type="button"
      onClick={onOpen}
      title="Ask Claude about this page"
      aria-label="Ask Claude"
      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition-colors hover:bg-surface-2"
    >
      <RailMark size={18} />
    </button>
  )
}

export function ClaudePanel({
  surfaceId,
  surfaceLabel,
  screens,
  onClose,
}: {
  surfaceId: string | undefined
  surfaceLabel: string | undefined
  screens: string[]
  onClose: () => void
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const c = instanceClaude
  if (!c) return null
  const pageAsks = (surfaceId ? c.pageAsks?.[surfaceId as SurfaceId] : undefined) ?? []
  const asks = pageAsks.length ? pageAsks : c.asks.slice(0, 3)
  const hasGuide = activeInstance.modules.includes('learn' as SurfaceId)

  return (
    <>
      <div className="oc-scrim" onClick={onClose} />
      <aside className="oc-panel" role="dialog" aria-label="Ask Claude">
        <div className="oc-panel-head">
          <div className="min-w-0 flex-1">
            {/* A customer front door is the customer's own product, never "your
                Command Centre" (Sam, 16 Oct 2026). Build-time: every other
                build keeps the stock words. */}
            <p className="oc-kicker">{__FRONT_DOOR__ ? `Claude, beside ${productName}` : 'Claude, beside your Command Centre'}</p>
            <h2 className="m-0 text-[20px] font-bold leading-tight">This page shows it. Claude does it.</h2>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="rounded-lg p-1.5 text-text-muted hover:bg-surface-2">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="oc-panel-body">
          <MissionControl screens={screens} example={c.example} compact />

          <section>
            <p className="oc-kicker">{pageAsks.length && surfaceLabel ? `Ask about ${surfaceLabel}` : 'Try asking'}</p>
            <div className="grid gap-2.5">
              {asks.map((a) => (
                <AskCard key={a.title} a={a} open />
              ))}
            </div>
          </section>

          <button
            type="button"
            className="oc-built-btn"
            onClick={() => {
              onClose()
              openHowItsBuilt()
            }}
          >
            <Layers className="h-5 w-5" />
            <span>
              <b>{__FRONT_DOOR__ ? `How ${productName} is built` : 'How your Command Centre is built'}</b>
              <small>The parts behind the screens, and what Claude actually does. One picture.</small>
            </span>
          </button>

          <section>
            <p className="oc-kicker">Starting a chat</p>
            <ol className="oc-steps">
              <li>
                <span>1</span>
                <span>
                  Open Claude at <strong>claude.ai/code</strong> on your computer.
                </span>
              </li>
              <li>
                <span>2</span>
                <span>
                  Pick the <code>{c.folder}</code> folder
                  {c.environment ? (
                    <>
                      {' '}
                      and the <code>{c.environment}</code> environment
                    </>
                  ) : null}
                  . Every new chat.
                </span>
              </li>
              <li>
                <span>3</span>
                <span>Paste the request and press Enter. Whatever Claude changes shows up on these screens.</span>
              </li>
            </ol>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <a className="oc-link-btn" href={CLAUDE_URL} target="_blank" rel="noopener noreferrer">
                Open Claude <ArrowRight className="h-3.5 w-3.5" />
              </a>
              {hasGuide && (
                <button
                  type="button"
                  className="oc-ask-btn oc-ask-quiet"
                  onClick={() => {
                    onClose()
                    shellNavigate('/learn')
                  }}
                >
                  <BookOpen className="h-3.5 w-3.5" /> Everything you can ask
                </button>
              )}
            </div>
          </section>
        </div>
      </aside>
    </>
  )
}

// One panel for the whole shell, opened from the rail (desktop or phone) by a
// window event, so the rail never needs to hold its state.
const OPEN_EVENT = 'omni-claude:open'
export function openClaudePanel() {
  window.dispatchEvent(new Event(OPEN_EVENT))
}

/** Mounted once in the chrome: listens for openClaudePanel() and shows the panel for the current page. */
export function ClaudeHost({ surfaceId, surfaceLabel, screens }: { surfaceId?: string; surfaceLabel?: string; screens: string[] }) {
  const [open, setOpen] = useState(false)
  useEffect(() => {
    const on = () => setOpen(true)
    window.addEventListener(OPEN_EVENT, on)
    return () => window.removeEventListener(OPEN_EVENT, on)
  }, [])
  if (!instanceClaude) return null
  return (
    <>
      {open && <ClaudePanel surfaceId={surfaceId} surfaceLabel={surfaceLabel} screens={screens} onClose={() => setOpen(false)} />}
      <HowItsBuiltHost />
      <WelcomeHost screens={screens} />
    </>
  )
}
